import React, { useCallback, useEffect, useState } from 'react';
import StorefrontAPI from 'utilities/storefrontApi';

/**
 * The customer's half of support: ask something, and read the answer.
 *
 * The contact form already existed and is not this. That is a message into a
 * void — the reply arrives by email and the thread lives in somebody's inbox,
 * so a customer who asked "where is my order" has no way to see what they
 * asked, whether anybody read it, or what was said. Here it is all in one
 * place and it is theirs.
 *
 * Plain elements and inline styles, like the rest of the storefront: this
 * renders on the brand's own domain, outside our theme, and must paint the
 * colours the brand chose.
 */

const STATUS_LABEL = {
  open: 'Waiting for a reply',
  answered: 'Answered',
  closed: 'Closed',
};

export default function CustomerTickets({ brand, accent = '#422AFB' }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  // The "ask something" form, shown only when asked for: a support form
  // sitting open on an account page invites a ticket for things the page
  // itself already answers.
  const [asking, setAsking] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const load = useCallback(() => {
    if (!brand?._id) return;
    setLoading(true);
    StorefrontAPI.ticketList(brand._id)
      .then((rows) => setTickets(Array.isArray(rows) ? rows : []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [brand?._id]);

  useEffect(() => { load(); }, [load]);

  const ask = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    setBusy(true);
    try {
      await StorefrontAPI.ticketOpen(brand._id, { subject, body });
      setSubject(''); setBody(''); setAsking(false);
      load();
    } catch (_) { /* the form stays filled in, so nothing is retyped */ } finally {
      setBusy(false);
    }
  };

  const sendReply = async (ticketId) => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await StorefrontAPI.ticketReply(brand._id, ticketId, reply);
      setReply('');
      load();
    } catch (_) { /* as above */ } finally {
      setBusy(false);
    }
  };

  const heading = { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 };
  const card = {
    background: '#fff', borderRadius: 14, padding: 18,
    boxShadow: '0 1px 2px rgba(16,24,40,0.06)', marginTop: 16,
  };
  const input = {
    width: '100%', padding: '10px 12px', borderRadius: 10,
    border: '1px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h2 style={heading}>Help</h2>
        <button
          type='button'
          onClick={() => setAsking((v) => !v)}
          style={{
            background: asking ? 'transparent' : accent,
            color: asking ? '#64748b' : '#fff',
            border: asking ? '1px solid #e2e8f0' : 'none',
            borderRadius: 999, padding: '6px 14px',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {asking ? 'Cancel' : 'Ask a question'}
        </button>
      </div>

      {asking && (
        <form onSubmit={ask} style={{ marginTop: 14, display: 'grid', gap: 8 }}>
          <input
            style={input}
            placeholder='What is it about?'
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <textarea
            style={{ ...input, minHeight: 90, resize: 'vertical' }}
            placeholder='Tell us what you need…'
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <button
            type='submit'
            disabled={busy || !subject.trim() || !body.trim()}
            style={{
              justifySelf: 'start', background: accent, color: '#fff', border: 'none',
              borderRadius: 999, padding: '8px 18px', fontSize: 13, fontWeight: 600,
              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Sending…' : 'Send'}
          </button>
        </form>
      )}

      {loading ? (
        <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 14 }}>Loading…</p>
      ) : tickets.length === 0 ? (
        <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 14 }}>
          Nothing here yet. If something is wrong with an order, ask and it stays on this page.
        </p>
      ) : (
        <div style={{ marginTop: 14 }}>
          {tickets.map((t) => (
            <div key={t._id} style={{ borderTop: '1px solid #f1f5f9', padding: '12px 0' }}>
              <button
                type='button'
                onClick={() => { setOpenId(openId === t._id ? null : t._id); setReply(''); }}
                style={{
                  display: 'flex', width: '100%', justifyContent: 'space-between',
                  alignItems: 'center', gap: 12, background: 'none', border: 'none',
                  padding: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t.subject}</span>
                <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  {STATUS_LABEL[t.status] || t.status}
                </span>
              </button>

              {openId === t._id && (
                <div style={{ marginTop: 12 }}>
                  {(t.messages || []).map((m) => (
                    <div
                      key={m._id}
                      style={{
                        // Theirs on the right in the brand's colour, the
                        // brand's on the left — the way every messaging app
                        // people already use does it.
                        marginLeft: m.author === 'customer' ? 'auto' : 0,
                        maxWidth: '80%',
                        background: m.author === 'customer' ? accent : '#f1f5f9',
                        color: m.author === 'customer' ? '#fff' : '#0f172a',
                        borderRadius: 12, padding: '8px 12px', marginBottom: 8,
                      }}
                    >
                      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{m.body}</div>
                      <div style={{ fontSize: 10, opacity: 0.75, marginTop: 4 }}>
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}

                  <textarea
                    style={{ ...input, minHeight: 64, resize: 'vertical', marginTop: 4 }}
                    placeholder={t.status === 'closed'
                      ? 'Writing again will reopen this.'
                      : 'Write a reply…'}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                  />
                  <button
                    type='button'
                    onClick={() => sendReply(t._id)}
                    disabled={busy || !reply.trim()}
                    style={{
                      marginTop: 8, background: accent, color: '#fff', border: 'none',
                      borderRadius: 999, padding: '7px 16px', fontSize: 13, fontWeight: 600,
                      cursor: busy || !reply.trim() ? 'default' : 'pointer',
                      opacity: busy || !reply.trim() ? 0.6 : 1,
                    }}
                  >
                    Send
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
